import React, { useState, useCallback, useContext } from 'react';
import {
    Box,
    Button,
    Stepper,
    Step,
    StepLabel,
} from '@mui/material';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useHistory } from 'react-router-dom';
import {
    BasicInfoStep,
    AddressStep,
    CompanyTypeStep,
    AdminsStep,
    DocumentsStep
} from './steps';
import CompanyModel from '../../../../models/CompanyModel';
import { getBackendUserUrl } from 'utils/utils';
import { UserContext } from '../../../../contexts/UserContext';

/**
 * Main company onboarding form component
 */
const CompanyOnboardingForm = () => {    
    const history = useHistory();
    const { user } = useContext(UserContext);

    // Step state for pagination
    const [activeStep, setActiveStep] = useState(0);
    const steps = ['Basic Info', 'Address', 'Company Type', 'Administrators', 'Documents'];
    
    // Form state
    const [errors, setErrors] = useState({});
    
    // Step 1: Basic Information
    const [companyName, setCompanyName] = useState('');
    const [website, setWebsite] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [logo, setLogo] = useState(null);
    const [domain, setDomain] = useState('');
    
    // Step 2: Address
    const [addresses, setAddresses] = useState([]);
    
    // Step 3: Company Type
    const [companyTypes, setCompanyTypes] = useState([]);
    
    // Step 4: Admins
    const [admins, setAdmins] = useState([]);
    
    // Step 5: Documents
    const [documents, setDocuments] = useState({});
    
    // Validation functions
    const validateStep1 = () => {
        const newErrors = {};
        let isValid = true;
        
        if (!companyName?.trim()) {
            newErrors.companyName = 'Company name is required';
            isValid = false;
        }
        
        if (!phoneNumber?.trim()) {
            newErrors.phoneNumber = 'Phone number is required';
            isValid = false;
        }
        
        if (!domain?.trim()) {
            newErrors.domain = 'Domain is required';
            isValid = false;
        }

        // Only validate logo if it hasn't been uploaded
        if (!logo || !(logo instanceof File)) {
            newErrors.logo = 'Company logo is required';
            isValid = false;
        }
        
        setErrors(prev => ({ ...prev, ...newErrors }));
        return isValid;
    };
    
    const validateStep2 = () => {
        if (addresses.length === 0) {
            setErrors(prev => ({
                ...prev,
                addresses: 'At least one address is required'
            }));
            return false;
        }
        
        const newErrors = { addresses: {} };
        let isValid = true;
        
        addresses.forEach((address, index) => {
            newErrors.addresses[index] = {};
            
            if (!address.street?.trim()) {
                newErrors.addresses[index].street = 'Street is required';
                isValid = false;
            }
            
            if (!address.city?.trim()) {
                newErrors.addresses[index].city = 'City is required';
                isValid = false;
            }
            
            if (!address.state?.trim()) {
                newErrors.addresses[index].state = 'State is required';
                isValid = false;
            }
            
            if (!address.zipCode?.trim()) {
                newErrors.addresses[index].zipCode = 'ZIP code is required';
                isValid = false;
            }
        });
        
        setErrors(prev => ({ ...prev, ...newErrors }));
        return isValid;
    };
    
    const validateStep3 = () => {
        if (companyTypes.length === 0) {
            setErrors(prev => ({
                ...prev,
                companyTypes: 'Please select at least one company type'
            }));
            return false;
        }
        
        return true;
    };
    
    const validateStep4 = () => {
        // TODO: Add logic to check if the primary admin uses valid email
        return true;
    };
    
    const validateStep5 = () => {
        // TODO: In real pilot, if we see that comapnies are using the documents, we can add them here as required
        return true;
    };
    
    // Document change handler
    const handleDocumentChange = useCallback((documentType, action, value) => {
        setDocuments(prev => {
            const updatedDocuments = { ...prev };
            
            if (action === 'add') {
                updatedDocuments[documentType] = [
                    ...(updatedDocuments[documentType] || []),
                    ...value
                ];
            } else if (action === 'remove') {
                updatedDocuments[documentType] = (updatedDocuments[documentType] || [])
                    .filter((_, index) => index !== value);
            }
            
            return updatedDocuments;
        });
    }, []);
    
    // Handle next button click
    const handleNext = () => {
        switch (activeStep) {
            case 0: // Basic Information
                if (validateStep1()) {
                    setActiveStep(1);
                } else {
                    toast.error('Please complete all required basic information fields');
                }
                break;
            case 1: // Address
                if (validateStep2()) {
                    setActiveStep(2);
                } else {
                    toast.error('Please fill in all required address fields');
                }
                break;
            case 2: // Company Type
                if (validateStep3()) {
                    setActiveStep(3);
                } else {
                    toast.error('Please select at least one company type');
                }
                break;
            case 3: // Admins
                if (validateStep4()) {
                    setActiveStep(4);
                } else {
                    toast.error('Please add at least one administrator with your login email');
                }
                break;
            case 4: // Documents
                if (validateStep5()) {
                    handleSubmit();
                } else {
                    toast.error('Please upload all required documents');
                }
                break;
            default:
                break;
        }
    };

    // Handle back button click
    const handleBack = () => {
        setActiveStep(activeStep - 1);
    };
    
    /**
     * Determine the next onboarding route based on selected company types
     * @returns {string} The route to navigate to
     */
    const getNextOnboardingRoute = () => {
        const hasVenue = companyTypes.includes('Venue');
        const hasPackageServices = companyTypes.some(type => 
            ['Catering', 'Sponsor', 'Other Service Provider'].includes(type)
        );
        const onlyHost = companyTypes.length === 1 && companyTypes.includes('Host');
        
        // Store the company types in sessionStorage to access in the next onboarding step
        sessionStorage.setItem('selectedCompanyTypes', JSON.stringify(companyTypes));
        
        if (onlyHost) {
            return '/myevents'; // Go directly to myevents if only Host is selected
        } else if (hasVenue && hasPackageServices) {
            return '/venueOwner/venueOnboardingForm'; // Venue first, then packages
        } else if (hasVenue) {
            return '/venueOwner/venueOnboardingForm'; // Only venue
        } else if (hasPackageServices) {
            return '/packagesOnboardingForm'; // Only packages
        } else {
            return '/myevents'; // Default fallback
        }
    };
    
    // Handle form submission
    const handleSubmit = async () => {
        // Log user ID
        
        
        // Create company data object following the CompanyModel structure
        const companyData = {
            ...CompanyModel,
            companyName,
            website,
            phoneNumber,
            logo,
            addresses,
            companyTypes,
            admins,
            documents,
            domain,
            userId: user?.userId // added as contactId for the company, and also as an admin in the Users table.
        };

        // Prepare FormData for file uploads
        const formData = new FormData();
        // Add the JSON data (without file objects)
        const companyDataForJSON = { ...companyData };

        // Remove logo and documents from JSON, handle separately
        delete companyDataForJSON.logo;
        delete companyDataForJSON.documents;
        formData.append('companyData', JSON.stringify({
            ...companyDataForJSON,
            documents: {} // We'll fill this in with file names on the backend
        }));

        // Add logo file if present
        if (companyData.logo instanceof File) {
            formData.append('logo', companyData.logo, companyData.logo.name);
        }

        // Add document files
        if (companyData.documents) {
            Object.entries(companyData.documents).forEach(([docType, files]) => {
                if (Array.isArray(files)) {
                    files.forEach((file, idx) => {
                        if (file instanceof File) {
                            formData.append(`documents__${docType}__${idx}`, file, file.name);
                        }
                    });
                }
            });
        }

        // Show loading toast
        const loadingToast = toast.loading('Submitting company...');
        try {
            const apiUrl = getBackendUserUrl() + '/company/add_company';
            const response = await fetch(apiUrl, {
                method: 'POST',
                body: formData,
                credentials: 'include',
            });

            toast.dismiss(loadingToast);

            if (!response.ok) {
                let errorMessage = 'Failed to submit company';
                try {
                    const errorData = await response.json();
                    errorMessage = errorData.detail || errorMessage;
                } catch (parseError) {}
                toast.error(errorMessage);
                return;
            }

            const result = await response.json();
            console.log('Company submitted successfully:', result);
            toast.success('Company successfully registered!');
            
            // Store the company ID in sessionStorage for use in subsequent onboarding forms
            if (result.company_id) {
                sessionStorage.setItem('companyId', result.company_id);
            }
            
            // Determine and navigate to the next route based on company types
            const nextRoute = getNextOnboardingRoute();
            history.push(nextRoute);
            
        } catch (error) {
            toast.dismiss(loadingToast);
            console.error('Error submitting company:', error);
            toast.error('Failed to submit company. Please try again.');
        }
    };

    // Render the appropriate step based on activeStep
    const renderStep = () => {
        switch (activeStep) {
            case 0: // Basic Information
                return (
                    <BasicInfoStep 
                        companyName={companyName}
                        setCompanyName={setCompanyName}
                        website={website}
                        setWebsite={setWebsite}
                        phoneNumber={phoneNumber}
                        setPhoneNumber={setPhoneNumber}
                        logo={logo}
                        setLogo={setLogo}
                        domain={domain}
                        setDomain={setDomain}
                        errors={errors}
                    />
                );
            case 1: // Address
                return (
                    <AddressStep 
                        addresses={addresses}
                        setAddresses={setAddresses}
                        errors={errors}
                    />
                );
            case 2: // Company Type
                return (
                    <CompanyTypeStep 
                        companyTypes={companyTypes}
                        setCompanyTypes={setCompanyTypes}
                        errors={errors}
                    />
                );
            case 3: // Admins
                return (
                    <AdminsStep 
                        admins={admins}
                        setAdmins={setAdmins}
                        errors={errors}
                    />
                );
            case 4: // Documents
                return (
                    <DocumentsStep 
                        companyTypes={companyTypes}
                        documents={documents}
                        onDocumentChange={handleDocumentChange}
                        errors={errors}
                    />
                );
            default:
                return null;
        }
    };

    return (
        <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3, mt: '70px' }}>
            <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
                {steps.map((label) => (
                    <Step key={label}>
                        <StepLabel>{label}</StepLabel>
                    </Step>
                ))}
            </Stepper>

            {renderStep()}

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4 }}>
                {activeStep > 0 && (
                    <Button 
                        variant="outlined" 
                        onClick={handleBack}
                        sx={{ mr: 2 }}
                    >
                        Back
                    </Button>
                )}
                
                <Button 
                    variant="contained" 
                    color="primary"
                    onClick={handleNext}
                >
                    {activeStep === steps.length - 1 ? 'Submit' : 'Next'}
                </Button>
            </Box>
        </Box>
    );
};

export default CompanyOnboardingForm;
